import { useEffect, useMemo, useRef, useState } from "react";
import { PageHeader } from "../components/layout";
import { Button } from "../components/ui";
import { ContextBar } from "../components/context-bar";
import { SafeError } from "../components/phase4";
import { ApiUnavailableState, EmptyState, NotConfiguredState } from "../components/states";
import {
  AllowanceStrip,
  BALDER_CANNOT_DEPLOY_TEXT,
  ConfirmationCard,
  NO_DISCORD_CHANGES_TEXT,
  ToolActivityCard,
  looksLikeSecret,
} from "../components/balder";
import { SupportLink } from "../components/support-link";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type BalderAllowance,
  type BalderConfirmation,
  type BalderConversation,
  type BalderStreamEvent,
  type BalderToolActivity,
  type BalderUsage,
} from "../lib/api";

// The client-side secret guard message. Best-effort — the server also blocks it.
export const SECRET_WARNING_TEXT =
  "This message looks like it contains a bot token or secret. Remove it before sending — never paste tokens into chat.";

/** A message as rendered in the thread (streaming-aware). */
export interface BalderMessageVM {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  streaming?: boolean;
  failed?: string | null;
  tools?: BalderToolActivity[];
  /**
   * Set when the turn completed without an answer because a limit stopped it
   * (allowance, rate limit, secret guard). The streamed text is the explanation,
   * so it is rendered as a notice — presenting it as BALDER's reply would make a
   * refusal look like a bad answer.
   */
  blockedReason?: string | null;
}

// ── Presentational workspace (no data fetching; unit-testable) ────────────────
export interface BalderWorkspaceViewProps {
  providerAvailable: boolean;
  sessionExpired?: boolean;
  conversations: BalderConversation[];
  activeConversationId: string | null;
  activeConversation?: BalderConversation | null;
  context?: { tenantName?: string | null; guildName?: string | null; blueprintName?: string | null };
  messages: BalderMessageVM[];
  streaming: boolean;
  pendingConfirmation: BalderConfirmation | null;
  usage?: BalderUsage | null;
  /** Current-period BALDER budget. `null` = unmetered or not yet known. */
  allowance?: BalderAllowance | null;
  allowanceLoading?: boolean;
  allowanceError?: string | null;
  composerValue: string;
  actionBusy?: boolean;
  /** True while a New Chat request is in flight — the button stays disabled. */
  creatingConversation?: boolean;
  errorMessage?: string | null;
  onSelectConversation?: (id: string) => void;
  onNewConversation?: () => void;
  onRenameConversation?: (id: string) => void;
  onArchiveConversation?: (id: string) => void;
  onComposerChange?: (value: string) => void;
  onSend?: () => void;
  onCancel?: () => void;
  onConfirm?: (actionId: string) => void;
  onReject?: (actionId: string) => void;
  onRetry?: () => void;
}

export function BalderWorkspaceView(props: BalderWorkspaceViewProps): JSX.Element {
  const {
    providerAvailable,
    sessionExpired,
    conversations,
    activeConversationId,
    activeConversation,
    context,
    messages,
    streaming,
    pendingConfirmation,
    usage,
    allowance,
    allowanceLoading,
    allowanceError,
    composerValue,
    actionBusy,
    creatingConversation,
    errorMessage,
    onSelectConversation,
    onNewConversation,
    onRenameConversation,
    onArchiveConversation,
    onComposerChange,
    onSend,
    onCancel,
    onConfirm,
    onReject,
    onRetry,
  } = props;

  const secretWarned = looksLikeSecret(composerValue);
  const canSend = !streaming && !secretWarned && composerValue.trim().length > 0 && !!activeConversationId;

  if (sessionExpired) {
    return (
      <div className="balder-state" role="alert">
        <p className="state__title">Your session has expired</p>
        <p>Sign in again to keep working with BALDER. {NO_DISCORD_CHANGES_TEXT}</p>
        <div className="row">
          <a className="btn btn--primary" href="/login">
            Sign in again
          </a>
          {onRetry ? (
            <Button variant="ghost" onClick={onRetry}>
              Retry
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  if (!providerAvailable) {
    return (
      <div className="balder-state balder-state--down" role="alert">
        <p className="state__title">BALDER is currently unavailable</p>
        <p>
          The BALDER assistant provider can’t be reached right now. Your workspace, blueprints and servers are
          unaffected — {NO_DISCORD_CHANGES_TEXT} {BALDER_CANNOT_DEPLOY_TEXT}
        </p>
        {onRetry ? (
          <Button variant="secondary" onClick={onRetry}>
            Retry connection
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="balder-workspace">
      {/* ── Conversation list ─────────────────────────────────────────────── */}
      <aside className="balder-sidebar" aria-label="Conversations">
        <div className="balder-sidebar__head">
          <span className="field__label">Conversations</span>
          {/*
            Spec 2.2: "Prevent duplicate creation from double clicks." Disabling
            while the request is in flight is the guard — an impatient second
            click would otherwise leave a stray empty conversation in the list
            that the customer has to clean up.
          */}
          <Button variant="secondary" disabled={creatingConversation} onClick={() => onNewConversation?.()}>
            {creatingConversation ? "Starting…" : "New"}
          </Button>
        </div>
        {conversations.length === 0 ? (
          <p className="muted">No conversations yet.</p>
        ) : (
          <ul className="balder-conv-list">
            {conversations.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={c.id === activeConversationId ? "balder-conv balder-conv--active" : "balder-conv"}
                  aria-current={c.id === activeConversationId ? "true" : undefined}
                  onClick={() => onSelectConversation?.(c.id)}
                >
                  <span className="balder-conv__title">{c.title || "Untitled conversation"}</span>
                  {c.status === "ARCHIVED" ? <span className="muted"> · archived</span> : null}
                </button>
                <span className="balder-conv__actions">
                  <button type="button" className="linkish" onClick={() => onRenameConversation?.(c.id)}>
                    Rename
                  </button>
                  <button type="button" className="linkish" onClick={() => onArchiveConversation?.(c.id)}>
                    Archive
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* ── Active conversation ───────────────────────────────────────────── */}
      <section className="balder-main">
        <div className="balder-context" aria-label="Conversation context">
          <span className="badge">Workspace: {context?.tenantName ?? "—"}</span>
          <span className="badge">Server: {context?.guildName ?? activeConversation?.guildName ?? "—"}</span>
          <span className="badge">Blueprint: {context?.blueprintName ?? activeConversation?.blueprintName ?? "none"}</span>
        </div>

        {/*
          Spec 2.3: the allowance is shown always — not only once it runs out.
          Discovering a limit by being refused mid-task is the failure this
          prevents, so it sits above the thread on every state of the page.
        */}
        <AllowanceStrip allowance={allowance ?? null} loading={allowanceLoading} error={allowanceError ?? null} />

        {conversations.length === 0 ? (
          <BalderIntroduction onStart={onNewConversation} busy={creatingConversation} />
        ) : !activeConversationId ? (
          <EmptyState title="Start a conversation with BALDER">
            BALDER can inspect a server, draft blueprint changes and preview a deployment. It never changes Discord and{" "}
            {BALDER_CANNOT_DEPLOY_TEXT.toLowerCase()}
          </EmptyState>
        ) : (
          <>
            <div className="balder-thread" aria-live="polite">
              {messages.length === 0 ? (
                <p className="muted">Send a message to begin. {NO_DISCORD_CHANGES_TEXT}</p>
              ) : (
                messages.map((m) => <MessageBubble key={m.id} message={m} />)
              )}
            </div>

            {pendingConfirmation ? (
              <ConfirmationCard
                confirmation={pendingConfirmation}
                busy={actionBusy}
                onConfirm={onConfirm}
                onReject={onReject}
              />
            ) : null}

            {errorMessage ? <SafeError message={errorMessage} /> : null}

            {/* ── Composer ─────────────────────────────────────────────────── */}
            <div className="balder-composer">
              {secretWarned ? (
                <p className="balder-secret-warning" role="alert">
                  {SECRET_WARNING_TEXT}
                </p>
              ) : null}
              <textarea
                className="balder-composer__input"
                aria-label="Message BALDER"
                placeholder="Ask BALDER to inspect a server or draft a blueprint change…"
                value={composerValue}
                rows={3}
                onChange={(e) => onComposerChange?.(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSend) {
                    e.preventDefault();
                    onSend?.();
                  }
                }}
              />
              <div className="balder-composer__actions">
                <span className="muted balder-usage">
                  {usage?.totalTokens != null ? `${usage.totalTokens} tokens this turn` : " "}
                </span>
                {streaming ? (
                  <Button variant="secondary" onClick={() => onCancel?.()}>
                    Cancel
                  </Button>
                ) : (
                  <Button variant="primary" disabled={!canSend} onClick={() => onSend?.()}>
                    Send
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

/**
 * Spec 2.1: "Show an introduction when no chats exist."
 *
 * A brand-new workspace otherwise lands on an empty sidebar and an empty pane
 * with no indication of what BALDER is for or that starting costs nothing. The
 * limits are stated here rather than discovered later.
 */
function BalderIntroduction({ onStart, busy }: { onStart?: () => void; busy?: boolean }): JSX.Element {
  return (
    <div className="balder-intro" aria-label="About BALDER">
      <h3>Meet BALDER, your ODIN companion</h3>
      <p>
        BALDER reads your workspace and helps you plan changes: it can inspect a connected server, explain what is
        currently configured, draft blueprint changes and preview a deployment before anything happens.
      </p>
      <p>
        <strong>{NO_DISCORD_CHANGES_TEXT}</strong> BALDER is advisory — it writes drafts and previews inside ODIN.{" "}
        {BALDER_CANNOT_DEPLOY_TEXT} A human approves every deployment on the release review page.
      </p>
      <p className="muted">
        Starting a chat does not buy anything. Your workspace&rsquo;s included BALDER allowance is shown above, and
        BALDER stops when it runs out rather than charging you.
      </p>
      {/* Part 10.2 — BALDER escalation. BALDER is advisory and cannot act on Discord,
          approve a deployment, or touch money. When the answer a customer needs is one
          BALDER is not permitted to give, the honest next step is a person — not a
          differently-worded prompt. */}
      <p className="muted">
        Need something BALDER cannot do, or a human answer?{" "}
        <SupportLink topic="BALDER">Message support</SupportLink>.
      </p>
      <Button variant="primary" disabled={busy} onClick={() => onStart?.()}>
        {busy ? "Starting…" : "Start your first chat"}
      </Button>
    </div>
  );
}

function MessageBubble({ message }: { message: BalderMessageVM }): JSX.Element {
  if (message.blockedReason) {
    // Not an answer — a limit explanation. Rendering it as an assistant reply
    // would read as BALDER giving a strange response to the question.
    return (
      <div className="balder-msg balder-msg--notice" role="status">
        <span className="balder-msg__role">ODIN</span>
        <p className="balder-msg__content">{message.content}</p>
      </div>
    );
  }
  return (
    <div className={`balder-msg balder-msg--${message.role}`}>
      <span className="balder-msg__role">{message.role === "assistant" ? "BALDER" : message.role}</span>
      {message.content ? (
        <p className="balder-msg__content">
          {message.content}
          {message.streaming ? <span className="balder-caret" aria-hidden="true" /> : null}
        </p>
      ) : message.streaming ? (
        <p className="muted">BALDER is thinking…</p>
      ) : null}
      {(message.tools ?? []).length > 0 ? (
        <div className="balder-tools">
          {message.tools!.map((t) => (
            <ToolActivityCard key={t.id || t.kind} tool={t} />
          ))}
        </div>
      ) : null}
      {message.failed ? <p className="tool-card__error">{message.failed}</p> : null}
    </div>
  );
}

// ── Data-fetching container ───────────────────────────────────────────────────
let localIdSeq = 0;
const nextLocalId = (prefix: string) => `${prefix}-${Date.now()}-${localIdSeq++}`;

export function Balder(): JSX.Element {
  const ctx = useAppContext();
  const [conversations, setConversations] = useState<BalderConversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<BalderMessageVM[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<BalderConfirmation | null>(null);
  const [usage, setUsage] = useState<BalderUsage | null>(null);
  const [allowance, setAllowance] = useState<BalderAllowance | null>(null);
  const [allowanceLoading, setAllowanceLoading] = useState(false);
  const [allowanceError, setAllowanceError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [composer, setComposer] = useState("");
  const [providerAvailable, setProviderAvailable] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const streamMsgIdRef = useRef<string | null>(null);

  const ready = !!ctx.tenantId;
  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeId) ?? null,
    [conversations, activeId],
  );

  const handleApiError = (e: unknown, fallback: string): string => {
    if (e instanceof ApiError && e.status === 401) {
      setSessionExpired(true);
      return "Your session has expired.";
    }
    return e instanceof ApiError ? e.message : fallback;
  };

  const loadList = () => {
    if (!ctx.tenantId) return;
    setListError(null);
    api.balder
      .listConversations(ctx.tenantId)
      .then((r) => {
        setConversations(r.conversations);
        setProviderAvailable(true);
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) setSessionExpired(true);
        else if (e instanceof ApiError && (e.status === 502 || e.status === 503)) setProviderAvailable(false);
        setListError(e instanceof ApiError ? e.message : "Could not load conversations");
      });
  };

  /**
   * The allowance is loaded up front, not on first refusal. Someone about to
   * spend their last three requests on a long question deserves to know that
   * before they type it, and a "you have run out" that arrives only after the
   * work is written reads as the product losing their input.
   *
   * A failure here is reported as an unreadable meter, never as zero: telling a
   * paying customer their allowance is gone because a request timed out is the
   * one wrong answer that costs them something.
   */
  const loadAllowance = () => {
    if (!ctx.tenantId) return;
    setAllowanceLoading(true);
    setAllowanceError(null);
    api.balder
      .getUsage(ctx.tenantId)
      .then((r) => {
        setAllowance(r.allowance);
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) setSessionExpired(true);
        setAllowanceError(e instanceof ApiError ? e.message : "Could not read your BALDER allowance");
      })
      .finally(() => {
        setAllowanceLoading(false);
      });
  };

  useEffect(() => {
    if (!ready) {
      setConversations([]);
      setActiveId(null);
      setMessages([]);
      setAllowance(null);
      setAllowanceError(null);
      return;
    }
    loadList();
    loadAllowance();
  }, [ctx.tenantId]); // loaders read the same context value

  const openConversation = async (id: string) => {
    if (!ctx.tenantId) return;
    setActiveId(id);
    setMessages([]);
    setPendingConfirmation(null);
    setError(null);
    try {
      const detail = await api.balder.getConversation(ctx.tenantId, id);
      setMessages(
        detail.messages.map((m) => ({ id: m.id, role: m.role, content: m.content, tools: m.tools })),
      );
    } catch (e) {
      setError(handleApiError(e, "Could not load this conversation"));
    }
  };

  const newConversation = async () => {
    if (!ctx.tenantId) return;
    // Guarded because the create endpoint is not idempotent: an impatient
    // double-click used to leave two empty conversations in the sidebar, one of
    // which the person then had to notice and clean up.
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const conv = await api.balder.createConversation(ctx.tenantId, {
        guildId: ctx.guildId,
      });
      setConversations((cs) => [conv, ...cs]);
      setActiveId(conv.id);
      setMessages([]);
      setPendingConfirmation(null);
    } catch (e) {
      setError(handleApiError(e, "Could not create a conversation"));
    } finally {
      setCreating(false);
    }
  };

  const renameConversation = async (id: string) => {
    if (!ctx.tenantId) return;
    const title = window.prompt("Rename conversation");
    if (title == null || title.trim().length === 0) return;
    try {
      const conv = await api.balder.renameConversation(ctx.tenantId, id, title.trim());
      setConversations((cs) => cs.map((c) => (c.id === id ? conv : c)));
    } catch (e) {
      setError(handleApiError(e, "Could not rename the conversation"));
    }
  };

  const archiveConversation = async (id: string) => {
    if (!ctx.tenantId) return;
    try {
      await api.balder.archiveConversation(ctx.tenantId, id);
      setConversations((cs) => cs.filter((c) => c.id !== id));
      if (activeId === id) {
        setActiveId(null);
        setMessages([]);
      }
    } catch (e) {
      setError(handleApiError(e, "Could not archive the conversation"));
    }
  };

  // Apply one normalized SSE event to local state.
  const applyEvent = (event: BalderStreamEvent) => {
    const streamId = streamMsgIdRef.current;
    switch (event.type) {
      case "text.delta":
        setMessages((ms) =>
          ms.map((m) => (m.id === streamId ? { ...m, content: m.content + event.delta } : m)),
        );
        break;
      case "tool.proposed":
      case "tool.started":
      case "tool.completed":
      case "tool.failed":
        setMessages((ms) =>
          ms.map((m) => {
            if (m.id !== streamId) return m;
            const tools = [...(m.tools ?? [])];
            const idx = tools.findIndex((t) => t.id && t.id === event.tool.id);
            if (idx >= 0) tools[idx] = { ...tools[idx], ...event.tool };
            else tools.push(event.tool);
            return { ...m, tools };
          }),
        );
        break;
      case "confirmation.required":
        setPendingConfirmation(event.confirmation);
        break;
      case "usage.updated":
        setUsage(event.usage);
        break;
      case "allowance.updated":
        // The server sends this alongside a refusal as well as after a normal
        // turn, so the number on screen is always the one the server just used
        // to decide — the display never disagrees with the behaviour.
        setAllowance(event.allowance);
        break;
      case "message.completed":
        setMessages((ms) =>
          ms.map((m) =>
            m.id === streamId
              ? {
                  ...m,
                  streaming: false,
                  content: event.content ?? m.content,
                  // A refusal is ODIN speaking, not BALDER answering badly. The
                  // reason travels with the message so the bubble can say which
                  // limit was hit instead of leaving a bare stop.
                  blockedReason: event.blocked ? (event.reason ?? "blocked") : null,
                }
              : m,
          ),
        );
        break;
      case "message.failed":
        setMessages((ms) =>
          ms.map((m) => (m.id === streamId ? { ...m, streaming: false, failed: event.message } : m)),
        );
        break;
      default:
        break;
    }
  };

  const send = async () => {
    if (!ctx.tenantId || !activeId) return;
    const content = composer.trim();
    if (content.length === 0 || looksLikeSecret(content)) return; // secret guard
    setError(null);
    setUsage(null);
    setComposer("");

    const userMsg: BalderMessageVM = { id: nextLocalId("u"), role: "user", content };
    const asstId = nextLocalId("a");
    streamMsgIdRef.current = asstId;
    setMessages((ms) => [...ms, userMsg, { id: asstId, role: "assistant", content: "", streaming: true }]);

    const controller = new AbortController();
    abortRef.current = controller;
    setStreaming(true);
    try {
      // Body field is `message` — the server 400s on anything else. This read
      // `{ content }`, which meant every message a customer typed was rejected
      // before it reached the model. Pinned by a test in Balder.test.tsx.
      await api.balder.streamMessage(ctx.tenantId, activeId, { message: content }, applyEvent, controller.signal);
      setMessages((ms) => ms.map((m) => (m.id === asstId ? { ...m, streaming: false } : m)));
    } catch (e) {
      if (controller.signal.aborted) {
        setMessages((ms) =>
          ms.map((m) => (m.id === asstId ? { ...m, streaming: false, failed: "Cancelled." } : m)),
        );
      } else if (e instanceof ApiError && (e.status === 502 || e.status === 503)) {
        setProviderAvailable(false);
      } else {
        const msg = handleApiError(e, "BALDER could not respond");
        setMessages((ms) => ms.map((m) => (m.id === asstId ? { ...m, streaming: false, failed: msg } : m)));
        setError(msg);
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const cancel = async () => {
    abortRef.current?.abort();
    if (ctx.tenantId && activeId) {
      try {
        await api.balder.cancel(ctx.tenantId, activeId);
      } catch {
        // best-effort; the local abort already stopped the stream
      }
    }
  };

  const confirmAction = async (actionId: string) => {
    if (!ctx.tenantId) return;
    setActionBusy(true);
    try {
      await api.balder.confirmAction(ctx.tenantId, actionId);
      setPendingConfirmation(null);
    } catch (e) {
      setError(handleApiError(e, "Could not confirm the action"));
    } finally {
      setActionBusy(false);
    }
  };

  const rejectAction = async (actionId: string) => {
    if (!ctx.tenantId) return;
    setActionBusy(true);
    try {
      await api.balder.rejectAction(ctx.tenantId, actionId);
      setPendingConfirmation(null);
    } catch (e) {
      setError(handleApiError(e, "Could not reject the action"));
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="BALDER — AI companion">
        <p>
          A safe, advisory assistant. BALDER can inspect a server, draft blueprint changes and preview a deployment. It
          never changes Discord and {BALDER_CANNOT_DEPLOY_TEXT.toLowerCase()}
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to use BALDER.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar requireGuild />
          {!ready ? (
            <EmptyState title="Select a workspace">
              Choose a workspace above to open BALDER. A server and blueprint context is optional.
            </EmptyState>
          ) : listError && conversations.length === 0 && !sessionExpired && providerAvailable ? (
            <ApiUnavailableState detail={listError} />
          ) : (
            <BalderWorkspaceView
              providerAvailable={providerAvailable}
              sessionExpired={sessionExpired}
              conversations={conversations}
              activeConversationId={activeId}
              activeConversation={activeConversation}
              context={{
                tenantName: ctx.tenantName,
                guildName: ctx.guildName,
                blueprintName: activeConversation?.blueprintName ?? null,
              }}
              messages={messages}
              streaming={streaming}
              pendingConfirmation={pendingConfirmation}
              usage={usage}
              allowance={allowance}
              allowanceLoading={allowanceLoading}
              allowanceError={allowanceError}
              creatingConversation={creating}
              composerValue={composer}
              actionBusy={actionBusy}
              errorMessage={error}
              onSelectConversation={openConversation}
              onNewConversation={newConversation}
              onRenameConversation={renameConversation}
              onArchiveConversation={archiveConversation}
              onComposerChange={setComposer}
              onSend={send}
              onCancel={cancel}
              onConfirm={confirmAction}
              onReject={rejectAction}
              onRetry={() => {
                loadList();
                loadAllowance();
              }}
            />
          )}
        </>
      )}
    </>
  );
}
